import React, { useEffect, useMemo, useState, useRef } from 'react';
import { GET_ALL_BLOGS, GET_FILTERED_BLOGS } from './graphql/GET_BLOG_POSTS';
import { GET_CATEGORIES } from './graphql/GET_CATEGORIES';
import { GET_AUTHOR_ID } from './graphql/GET_AUTHOR_ID';
import { GET_CATEGORY_OR_TAG_ID } from './graphql/GET_AUTHOR_ID';
import { GET_BLOGS_BY_CATEGORY_AND_AUTHOR } from './graphql/GET_CATEGORIES';
import { GET_ALL_CATEGORIES } from './graphql/GET_CATEGORIES';
import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloProvider,
  useQuery,
} from '@apollo/client';
import {
  RichText,
  withSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import CursorPagination from './CursorPagination';
import SearchBar from './Search';
import { getDeliveryKey, getDeliveryUrl } from '../../lib/util';
import { useRouter } from 'next/router';
import { decodeHTMLEntities } from 'assets/utils/helper';
import PillList from 'components/atoms/PillList';
import Icons from 'components/atoms/Icons';

const AUTHORS_ROOT = '452CC16D-D332-4D0B-84A9-1C6CE9474839';
const CATEGORIES_ROOT = '2F3908C9-8A4D-444D-80B2-FC30C6E216D1';
//const TAGS_ROOT = '083404F5-686F-4428-8E47-AA454ABBB268';
const CATEGORIES_TEMPLATE = '38CF2D0D-C5FE-4BFD-84CD-E4EED14D0F94';
const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

const toGuid = (id: string) => {
  if (!id) return id as any;
  const s = id.replace(/[{}]/g, '').replace(/-/g, '').toLowerCase();
  return s.length === 32
    ? `${s.slice(0, 8)}-${s.slice(8, 12)}-${s.slice(12, 16)}-${s.slice(16, 20)}-${s.slice(20)}`
    : id;
};

const isLikelyGuid = (s?: string) =>
  !!s && /^[{}]?[0-9a-fA-F-]{32,38}[}]?$/.test(s || '');
const normalize = (s: string) =>
  decodeURIComponent(s || '')
    .replace(/\+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
const fixAmpEdgeCases = (s: string) => {
  if (!s) return s;
  let x = s;
  x = x.replace(/&amp;?/gi, '&');
  x = x.replace(/\bamp\b/gi, '&');
  x = x.replace(/(\S)&(\S)/g, '$1 & $2');
  x = x.replace(/\s*&\s*/g, ' & ');
  return x.replace(/\s+/g, ' ').trim();
};

const expectedKeys = new Set([
  'category-tag',
  'author',
  'page',
  'text',
  'after',
]);
const joinBrokenAmpersand = (query: Record<string, any>, baseKey: string) => {
  const base = (query[baseKey] as string) || '';
  if (!base) return base;
  const extras = Object.keys(query).filter(
    k => !expectedKeys.has(k) && (query as any)[k] === ''
  );
  return extras.length ? [base, ...extras].join('&') : base;
};

// Apollo Client Setup
const deliveryKey = getDeliveryKey();
const endpoint = getDeliveryUrl();
const client = new ApolloClient({
  link: new HttpLink({
    uri: endpoint,
    headers: { sc_apikey: deliveryKey },
  }),
  cache: new InMemoryCache(),
});

function useHorizontalWheelLock<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      const atEdges =
        el.scrollLeft <= 0 || el.scrollLeft >= el.scrollWidth - el.clientWidth;

      // Convert vertical wheel/trackpad to horizontal scrolling
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      } else if (!atEdges) {
        // If we're mid-rail, swallow horizontal wheel so body never scrolls
        e.preventDefault();
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel as any);
  }, []);

  return ref;
}

const ArticleList = (props: any) => {
  const { sitecoreContext } = props;
  const router = useRouter();
  const railRef = useHorizontalWheelLock<HTMLDivElement>();
  const pageSize = 12;
  const [searchText, setSearchText] = useState('');
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [cursorHistory, setCursorHistory] = useState<string[]>(['']);
  const [jumpLoading, setJumpLoading] = useState(false);
  const [pickedMoreCategory, setPickedMoreCategory] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const localSelectRef = useRef(false);
  const [dropdownLabel, setDropdownLabel] = useState('All Categories');
  const fields = sitecoreContext?.route?.fields;

  if (!router.isReady) return null;

  const urlSearchText = normalize((router.query.text as string) || '');
  useEffect(() => {
    setSearchText(urlSearchText);
  }, [urlSearchText]);

  const rawCategoryParamBrokenAware = joinBrokenAmpersand(
    router.query as any,
    'category-tag'
  );
  const rawAuthorParam = (router.query.author as string) || '';

  const authorParamIsId = isLikelyGuid(rawAuthorParam);
  const authorName = authorParamIsId ? '' : normalize(rawAuthorParam);

  const categoryParamIsId = isLikelyGuid(rawCategoryParamBrokenAware);
  const normalizedCt = categoryParamIsId
    ? ''
    : normalize(rawCategoryParamBrokenAware);
  const categoryOrTagName = categoryParamIsId
    ? ''
    : fixAmpEdgeCases(decodeHTMLEntities(normalizedCt)).trim();
  const categoryOrTagNameHtml = categoryOrTagName.replace(/&/g, '&amp;');

  useEffect(() => {
    const onOutSide = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node))
        setMoreOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('mousedown', onOutSide);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onOutSide);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  // Lookups
  const {
    data: authorLookup,
    loading: authorLoading,
    error: authorErr,
  } = useQuery(GET_AUTHOR_ID, {
    variables: { authorsRootPath: AUTHORS_ROOT, name: authorName },
    skip: !authorName,
    fetchPolicy: 'network-only',
  });

  const {
    data: ctLookup,
    loading: ctLoading,
    error: ctErr,
  } = useQuery(GET_CATEGORY_OR_TAG_ID, {
    variables: {
      categoriesRoot: CATEGORIES_ROOT,
      name: categoryOrTagName,
      nameHtml: categoryOrTagNameHtml,
    },
    skip: !categoryOrTagName,
    fetchPolicy: 'network-only',
  });

  const {
    data: categoriesData,
    loading: catsLoading,
    error: catsErr,
  } = useQuery(GET_ALL_CATEGORIES, {
    variables: {
      categoryPath: CATEGORIES_ROOT,
      categoryTemplateId: CATEGORIES_TEMPLATE,
    },
    fetchPolicy: 'cache-first',
  });

  const allCategories = categoriesData?.categories?.results ?? [];

  const top4Raw = Array.isArray(fields?.Top4Categories)
    ? (fields.Top4Categories as any[])
    : [];

  const top4Categories = useMemo(() => {
    if (!top4Raw.length) return [];

    const configuredIds = new Set(top4Raw.map((c: any) => toGuid(c.id)));

    return allCategories.filter((c: any) => configuredIds.has(toGuid(c.id)));
  }, [top4Raw, allCategories]);

  // Active category ID
  const rawCtParam =
    joinBrokenAmpersand(router.query as any, 'category-tag') || '';
  const resolvedCategoryOrTagId: string | null = categoryParamIsId
    ? toGuid(rawCategoryParamBrokenAware)
    : ctLookup?.search?.results?.[0]?.id
      ? toGuid(ctLookup.search.results[0].id)
      : null;

  const activeCategoryId: string = (() => {
    if (resolvedCategoryOrTagId) return resolvedCategoryOrTagId;
    if (!rawCtParam) return ''; // All
    const normalizedParam = fixAmpEdgeCases(
      decodeHTMLEntities(normalize(rawCtParam))
    );
    const match = allCategories.find(
      (c: any) => normalize(c.name) === normalize(normalizedParam)
    );
    return match?.id || '';
  })();

  useEffect(() => {
    if (localSelectRef.current) {
      localSelectRef.current = false;
      setPickedMoreCategory(null);
      return;
    }
    if (!rawCtParam) setDropdownLabel('All Categories');

    if (!rawCtParam) {
      setPickedMoreCategory(null);
      return;
    }

    if (!allCategories.length) return;

    const urlName = categoryOrTagName;
    const urlId = resolvedCategoryOrTagId;
    const found = urlId
      ? allCategories.find((c: any) => toGuid(c.id) === toGuid(urlId))
      : urlName
        ? allCategories.find(
            (c: any) => normalize(c.name) === normalize(urlName)
          )
        : null;

    const top4 = top4Categories.length
      ? top4Categories
      : allCategories.slice(0, 4);
    const isInTop4 = found
      ? top4.some((c: any) => toGuid(c.id) === toGuid(found.id))
      : false;

    if (found) {
      if (isInTop4) {
        setPickedMoreCategory(null);
      } else {
        setPickedMoreCategory({ id: toGuid(found.id), name: found.name });
      }
    } else {
      const syntheticId = urlId ?? `__synthetic__:${urlName || rawCtParam}`;
      const syntheticName = urlName || rawCtParam;
      setPickedMoreCategory({ id: syntheticId, name: syntheticName });
    }
  }, [router.query, allCategories, categoryOrTagName, resolvedCategoryOrTagId]);
  const moreCategories = useMemo(() => {
    const top4 = top4Categories.length
      ? top4Categories
      : allCategories.slice(0, 4);
    const top4Ids = new Set(top4.map((c: any) => toGuid(c.id)));
    return allCategories.filter((c: any) => !top4Ids.has(toGuid(c.id)));
  }, [allCategories, top4Categories]);

  const moreCategoriesFiltered = useMemo(() => {
    const injectedId = pickedMoreCategory
      ? toGuid(pickedMoreCategory.id)
      : null;
    return injectedId
      ? moreCategories.filter((c: any) => toGuid(c.id) !== injectedId)
      : moreCategories;
  }, [moreCategories, pickedMoreCategory]);

  const mapToPill = (c: any) => {
    const pillId = toGuid(c.id);
    const isActive = activeCategoryId && toGuid(activeCategoryId) === pillId;
    return {
      id: pillId,
      value: pillId,
      text: c.name,
      href: `/blog?category-tag=${encodeURIComponent(c.name.trim())}`,
      'data-active': isActive ? 'true' : undefined,
      'aria-current': isActive ? 'true' : undefined,
    };
  };

  const pillsWithAll = useMemo(() => {
    const top4 = top4Categories.length
      ? top4Categories
      : allCategories.slice(0, 4);
    const allActive = activeCategoryId === '' || !activeCategoryId;
    const allPill = {
      id: 'all',
      value: '',
      text: 'All',
      href: '/blog',
      'data-active': allActive ? 'true' : undefined,
      'aria-current': allActive ? 'true' : undefined,
    };

    if (pickedMoreCategory) {
      const pickedId = toGuid(pickedMoreCategory.id);
      const rest = top4
        .filter((c: any) => toGuid(c.id) !== pickedId)
        .slice(0, 3);
      const injected = {
        id: pickedId,
        value: pickedId,
        text: pickedMoreCategory.name,
        href: `/blog?category-tag=${encodeURIComponent(pickedMoreCategory.name.trim())}`,
        'data-active':
          activeCategoryId && toGuid(activeCategoryId) === pickedId
            ? 'true'
            : undefined,
        'aria-current':
          activeCategoryId && toGuid(activeCategoryId) === pickedId
            ? 'true'
            : undefined,
      };
      return [allPill, injected, ...rest.map(mapToPill)];
    }

    return [allPill, ...top4.map(mapToPill)];
  }, [allCategories, pickedMoreCategory, activeCategoryId]);

  // Author resolution
  const resolvedAuthorId: string | null = authorParamIsId
    ? toGuid(rawAuthorParam)
    : authorLookup?.search?.results?.[0]?.id
      ? toGuid(authorLookup.search.results[0].id)
      : null;

  const hasSearch = urlSearchText.trim().length > 0;
  const hasCategory = !!resolvedCategoryOrTagId;
  const hasAuthor = !!resolvedAuthorId;
  const bothIds = hasCategory && hasAuthor;
  const isFiltered = hasCategory || hasAuthor;

  // Choosing GraphQL query
  const activeQuery = useMemo(() => {
    if (hasSearch) return GET_FILTERED_BLOGS;
    if (bothIds) return GET_BLOGS_BY_CATEGORY_AND_AUTHOR;
    if (isFiltered) return GET_CATEGORIES;
    return GET_ALL_BLOGS;
  }, [hasSearch, bothIds, isFiltered]);

  // Reset pagination when filters change
  const filterKey = JSON.stringify({
    text: urlSearchText.trim(),
    author: resolvedAuthorId ?? null,
    category: resolvedCategoryOrTagId ?? null,
    mode: hasSearch
      ? 'search'
      : bothIds
        ? 'both'
        : isFiltered
          ? 'single'
          : 'all',
  });

  useEffect(() => {
    setEndCursor(null);
    setCursorHistory(['']);
    setPage(1);
  }, [filterKey, activeQuery]);

  // Block main query while lookups are loading
  const needAuthorLookup = !!authorName && !authorParamIsId;
  const needCtLookup = !!categoryOrTagName && !categoryParamIsId;

  const skipMainQuery =
    (needAuthorLookup && authorLoading) || (needCtLookup && ctLoading);

  // If lookup finished but failed to resolve an id, show No results
  const unresolvedButRequired =
    !hasSearch &&
    ((!!authorName && !authorLoading && !resolvedAuthorId) ||
      (!!categoryOrTagName && !ctLoading && !resolvedCategoryOrTagId));

  // Build main query variables
  const baseVars = {
    path: 'C8FFD0C1-12B0-475A-9CD9-E69075A5E472',
    pageSize,
    after: endCursor,
  };

  const variables = hasSearch
    ? { ...baseVars, searchValue: urlSearchText.trim() }
    : bothIds
      ? {
          ...baseVars,
          categoryId: resolvedCategoryOrTagId!,
          authorId: resolvedAuthorId!,
        }
      : isFiltered
        ? {
            ...baseVars,
            categoryId: resolvedCategoryOrTagId ?? 'null',
            authorId: resolvedAuthorId ?? 'null',
          }
        : baseVars;

  // Main query
  const { data, loading, error } = useQuery(activeQuery, {
    variables,
    fetchPolicy: 'network-only',
    skip: skipMainQuery || unresolvedButRequired,
  });

  useEffect(() => {
    if (error)
      console.error('Main query error:', JSON.stringify(error, null, 2));
    if (authorErr) console.warn('Author lookup failed:', authorErr);
    if (ctErr) console.warn('Category/Tag lookup failed:', ctErr);
  }, [error, authorErr, ctErr]);

  const total = data?.search?.total ?? 0;
  const totalPages = Math.ceil((data?.search?.total || 0) / pageSize);
  const safePage = total > 0 ? Math.min(Math.max(page, 1), totalPages) : 1;
  const startIndex = total > 0 ? (safePage - 1) * pageSize + 1 : 0;
  const endIndex = total > 0 ? Math.min(safePage * pageSize, total) : 0;

  // handlers
  const handleSearch = (text: string) => {
    const queryObj: Record<string, string> = {};
    if (text && text.trim().length > 0) queryObj.text = text.trim();
    const searchParams = new URLSearchParams(queryObj);
    const finalQuery = searchParams.toString().replace(/\+/g, '%20');
    const finalUrl = `/blog${finalQuery ? `?${finalQuery}` : ''}`;
    router.push(finalUrl, finalUrl, { shallow: true });
    scrollToTop();
  };

  const handleNext = () => {
    const nextCursor = data?.search?.pageInfo?.endCursor;
    if (nextCursor) {
      setCursorHistory(prev => [...prev, nextCursor]);
      setEndCursor(nextCursor);
      setPage(prev => prev + 1);
      scrollToTop();
    }
  };

  const handlePrevious = () => {
    if (cursorHistory.length > 1) {
      const newHistory = [...cursorHistory];
      newHistory.pop();
      const prevCursor = newHistory[newHistory.length - 1] || null;
      setCursorHistory(newHistory);
      setEndCursor(prevCursor);
      setPage(prev => Math.max(prev - 1, 1));
      scrollToTop();
    }
  };

  const handlePageClick = async (targetPage: number) => {
    if (targetPage === page) return;

    if (targetPage < page) {
      const newHistory = cursorHistory.slice(0, targetPage);
      const newCursor = newHistory[newHistory.length - 1] || null;
      setCursorHistory(newHistory);
      setEndCursor(newCursor);
      setPage(targetPage);
      scrollToTop();
      return;
    }

    setJumpLoading(true);
    const newHistory = [...cursorHistory];
    let tempCursor = endCursor;

    for (let i = page; i < targetPage; i++) {
      const result = await client.query({
        query: activeQuery,
        variables: {
          ...baseVars,
          after: tempCursor,
          ...(hasSearch && { searchValue: urlSearchText.trim() }),
          ...(bothIds && {
            categoryId: resolvedCategoryOrTagId!,
            authorId: resolvedAuthorId!,
          }),
          ...(!bothIds &&
            isFiltered && {
              categoryId: resolvedCategoryOrTagId ?? 'null',
              authorId: resolvedAuthorId ?? 'null',
            }),
        },
        fetchPolicy: 'network-only',
      });

      tempCursor = result.data?.search?.pageInfo?.endCursor;
      if (tempCursor) newHistory.push(tempCursor);
      else break;
    }

    setCursorHistory(newHistory);
    setEndCursor(newHistory[targetPage - 1] || null);
    setPage(targetPage);
    setJumpLoading(false);
    scrollToTop();
  };

  const formatDate = (sitecoreDate: string): string => {
    if (!sitecoreDate || sitecoreDate.length < 8) return '';
    const year = sitecoreDate.slice(0, 4);
    const month = sitecoreDate.slice(4, 6);
    const day = sitecoreDate.slice(6, 8);
    return `${month}/${day}/${year}`;
  };
  const applyCategory = (id: string) => {
    const selected =
      allCategories.find((c: any) => toGuid(c.id) === toGuid(id)) || null;
    localSelectRef.current = true;
    setDropdownLabel(selected ? selected.name : 'All Categories');
    setMoreOpen(false);

    if (!selected) {
      router.push('/blog', undefined, { shallow: true });
    } else {
      const encodedName = encodeURIComponent(selected.name.trim());
      router.push(`/blog?category-tag=${encodedName}`, undefined, {
        shallow: true,
      });
    }
    scrollToTop();
  };

  // Loading states
  if (
    (needAuthorLookup && authorLoading) ||
    (needCtLookup && ctLoading) ||
    loading ||
    jumpLoading
  ) {
    return <div className="text-center">Loading...</div>;
  }

  // No results
  if (skipMainQuery || unresolvedButRequired) {
    return (
      <div className="overhauled">
        <div className="blog-container">
          <SearchBar value={searchText} onSearch={handleSearch} />
          <div className="blog-intro-wrapper">
            <div className="blog-intro overhauled">
              <h1 className="osf-display-h1">
                {fields?.Title?.value || 'Blog'}
              </h1>
              <div className="blog-description">
                <RichText field={fields?.Description} />
              </div>
            </div>
          </div>
          <div className="pill-list">
            <div className="content-wrapper">
              {!catsLoading && !catsErr && (
                <div className="pill-row">
                  <div className="pill-rail" ref={railRef}>
                    <div className="pill-track">
                      <PillList type="category-tag" pills={pillsWithAll} />
                    </div>
                  </div>
                  {moreCategoriesFiltered.length > 0 && (
                    <div ref={moreRef} className="pill-dropdown">
                      <button
                        className={`selector ${activeCategoryId ? 'is-active' : ''}`}
                        onClick={() => setMoreOpen(prev => !prev)}
                        aria-expanded={moreOpen}
                        aria-label={dropdownLabel || 'Category Selector'}
                      >
                        <p className="default">{dropdownLabel}</p>
                        {moreOpen ? (
                          <Icons id="chevron-up" />
                        ) : (
                          <Icons id="chevron-down" />
                        )}
                      </button>

                      {moreOpen && (
                        <ul className="dropdown-menu">
                          {moreCategoriesFiltered.map((c: any) => (
                            <li
                              key={c.id}
                              className="dropdown-item"
                              onClick={() => applyCategory(c.id)}
                            >
                              {c.name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="article-listing overhauled">
            <div className="articles-grid empty">No results.</div>
          </div>
        </div>
      </div>
    );
  }

  const articles = data?.search?.results || [];
  //console.log("fields", fields?.Top4Categories?.value);

  return (
    <div
      className="overhauled"
      id={`article-listing-component-${props.rendering?.uid}`}
    >
      <div className="blog-container">
        <SearchBar value={searchText} onSearch={handleSearch} />
        <div className="blog-intro-wrapper">
          <div className="blog-intro overhauled">
            <h1 className="osf-display-h1">{fields?.Title?.value || 'Blog'}</h1>
            <div className="blog-description">
              <RichText field={fields?.Description} />
            </div>
          </div>
        </div>
        <div className="pill-list ">
          <div className="content-wrapper">
            {!catsLoading && !catsErr && (
              <div className="pill-row">
                <div className="pill-rail" ref={railRef}>
                  <div className="pill-track">
                    <PillList type="category-tag" pills={pillsWithAll} />
                  </div>
                </div>
                <div className="dropdown">
                  {moreCategoriesFiltered.length > 0 && (
                    <div ref={moreRef} className="pill-dropdown">
                      <button
                        className="selector"
                        onClick={() => setMoreOpen(prev => !prev)}
                        aria-expanded={moreOpen}
                        aria-label={dropdownLabel || 'Category Selector'}
                      >
                        <h2 className="dropdown-title">{dropdownLabel}</h2>
                        {moreOpen ? (
                          <Icons id="chevron-up" />
                        ) : (
                          <Icons id="chevron-down" />
                        )}
                      </button>

                      {moreOpen && (
                        <ul className="dropdown-menu">
                          {moreCategoriesFiltered.map((c: any) => (
                            <li
                              key={c.id}
                              className="dropdown-item"
                              onClick={() => applyCategory(c.id)}
                            >
                              {c.name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="article-listing overhauled">
          <div className="articles-grid">
            {articles.map((article: any) => (
              <a
                href={article.path.replace(
                  /^\/sitecore\/content\/osfhealthcare\/home\/blog/,
                  '/blog'
                )}
                key={article.id}
                className="article-card"
              >
                <div className="article-image">
                  {article.BlogImage?.value && (
                    <img
                      src={article.BlogImage.value}
                      alt={article.TitleRichText?.value || 'Article Image'}
                    />
                  )}
                </div>
                <div className="article-content">
                  <div className="article-date">
                    {formatDate(article.PublishedDate?.value || '')}
                  </div>
                  <h2
                    className="summary-title osf-display-h3"
                    dangerouslySetInnerHTML={{
                      __html: article.TitleRichText?.value || '',
                    }}
                  />
                </div>
              </a>
            ))}
          </div>
        </div>
        <div className="pagination-container">
          {data?.search?.total > pageSize && (
            <CursorPagination
              onNext={handleNext}
              onPrevious={handlePrevious}
              hasNext={data?.search?.pageInfo?.hasNext}
              page={page}
              totalPages={totalPages}
              onPageClick={handlePageClick}
            />
          )}

          <div className="result-count" aria-live="polite">
            {total > 0 && `Showing ${startIndex} - ${endIndex} of ${total}`}
          </div>
        </div>
      </div>
    </div>
  );
};

const WrappedArticleList = (props: any) => (
  <ApolloProvider client={client}>
    <ArticleList {...props} />
  </ApolloProvider>
);

export default withSitecoreContext()(WrappedArticleList);
